import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createPlaybackController,
  createSoundfontEngine,
  tickToSeconds,
  type SoundfontSource,
} from "@midi-fantom-utils/player-core";
import { buildPlaybackData, type Pattern } from "@midi-fantom-utils/midi-util";

export type PatternMiniPlayerProps = {
  pattern: Pattern;
  source: SoundfontSource;
};

/**
 * Мини-плеер для одного паттерна. Кнопка `▶`/`■` запускает/останавливает
 * воспроизведение через собственный экземпляр `createSoundfontEngine` +
 * `createPlaybackController`. Шарит `AudioContext` через модульный ref,
 * чтобы не плодить новые контексты на каждое нажатие ▶ (лимит браузера).
 *
 * Q10: допускается параллельное воспроизведение нескольких паттернов
 * одновременно с таймлайн-плеером — каждый мини-плеер создаёт свой
 * engine, но звучит через общий `masterGain`.
 */

// Один глобальный AudioContext + masterGain на сессию. Автополитика
// браузера ограничивает число контекстов; шеринг между мини-плеерами
// и TimelinePlayer безопасен.
let sharedAudioCtx: AudioContext | null = null;
let sharedMasterGain: GainNode | null = null;

const ensureSharedContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  if (sharedAudioCtx) return sharedAudioCtx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  const ctx = new Ctor();
  const gain = ctx.createGain();
  gain.gain.value = 0.8;
  gain.connect(ctx.destination);
  sharedAudioCtx = ctx;
  sharedMasterGain = gain;
  return ctx;
};

type Status = "idle" | "playing" | "ended" | "error";

export const PatternMiniPlayer = ({ pattern, source }: PatternMiniPlayerProps) => {
  const [audioCtx, setAudioCtx] = useState<AudioContext | null>(sharedAudioCtx);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const engineRef = useRef<ReturnType<typeof createSoundfontEngine> | null>(null);
  const controllerRef = useRef<ReturnType<typeof createPlaybackController> | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const playback = useMemo(() => buildPlaybackData(pattern.midi), [pattern.midi]);

  useEffect(() => {
    return () => {
      unsubRef.current?.();
      unsubRef.current = null;
      controllerRef.current?.dispose();
      controllerRef.current = null;
      engineRef.current?.dispose();
      engineRef.current = null;
    };
  }, []);

  const handlePlay = useCallback(async () => {
    let ctx = audioCtx ?? ensureSharedContext();
    if (!ctx) {
      setError("Web Audio не поддерживается");
      setStatus("error");
      return;
    }
    if (ctx.state === "suspended") await ctx.resume();
    if (!audioCtx) setAudioCtx(ctx);

    if (!engineRef.current) {
      const engine = createSoundfontEngine({
        audioCtx: ctx,
        source,
        notes: playback.notes,
        destination: sharedMasterGain ?? undefined,
        defer: true,
      });
      engineRef.current = engine;
      engine.setInput({
        source,
        notes: playback.notes,
        destination: sharedMasterGain ?? undefined,
      });
    }

    if (!controllerRef.current) {
      const controller = createPlaybackController({
        audioCtx: ctx,
        getNotes: () => playback.notes,
        tempos: playback.tempos,
        ppq: playback.ppq,
        onScheduleNote: (note, when) => {
          engineRef.current?.playNote(
            note,
            when,
            Math.max(
              0.05,
              tickToSeconds(note.durationTicks, playback.tempos, playback.ppq)
            )
          );
        },
        onStopAll: () => engineRef.current?.stopAll(),
      });
      const unsub = controller.subscribe(() => {
        setStatus(controller.getState().status as Status);
      });
      unsubRef.current = unsub;
      controllerRef.current = controller;
    }

    controllerRef.current.play();
  }, [audioCtx, source, playback]);

  const handleStop = useCallback(() => {
    controllerRef.current?.stop();
  }, []);

  const isPlaying = status === "playing";

  return (
    <button
      type="button"
      className="btn"
      style={{ padding: "2px 6px", fontSize: 11 }}
      onClick={isPlaying ? handleStop : handlePlay}
      aria-label={isPlaying ? "Стоп" : "Воспроизвести"}
      title={error ?? undefined}
    >
      {isPlaying ? "■" : "▶"}
    </button>
  );
};
